import config from '../../config.json'
import { hasProperty } from './object'

/**
 * A map of promises used by `loadScript()`
 * @type {Object<string, Promise>}
 * @private
 */
const loadScriptPromises = {}

/**
 * Returns the full URL for an image on the CDN server with the given path.
 *
 * @param {string} path
 * @return {string}
 */
export function getCdnImageUrl(path) {
  return `${config.cdnImgUrl}${path}`
}

/**
 * Returns the full URL for an image on the dynamic CDN server with the given ID.
 *
 * @param {number|string} id
 * @return {string}
 */
export function getDynCdnImageUrl(id) {
  return `${config.dynCdnImgUrl}/${id}`
}

/**
 * Returns the URL for the video with the given ID and file type extension.
 *
 * @param {string|number} id
 * @param {string} extension
 * @returns {string}
 */
export function getVideoUrl(id, extension) {
  return `${config.cdnVideoUrl}/${id}.${extension}`
}

/**
 * Loads the JS file with the given path and returns a promise which resolves once the file has been loaded.
 * If the same path is requested multiple times, the same promise will be returned each time.
 * If a script fails to load, the promise will be deleted after rejecting to prevent it from blocking any new attempts to load the file.
 *
 * This function is only to be used in browsers, since `document.head` is used internally.
 *
 * @param {string} path A path or full URL to the JS file to load.
 * @returns {Promise<>}
 */
export function loadScript(path) {
  if (hasProperty(loadScriptPromises, path)) {
    return loadScriptPromises[path]
  }

  const scriptTag = document.createElement('script')

  const promise = new Promise((resolve, reject) => {
    scriptTag.onload = resolve
    scriptTag.onerror = reject
    scriptTag.type = 'text/javascript'
    scriptTag.src = path

    document.head.appendChild(scriptTag)
  })

  loadScriptPromises[path] = promise

  promise.catch(() => {
    document.head.removeChild(scriptTag)
    delete loadScriptPromises[path]
  })

  return promise
}
